import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gear, CalendarBlank, CheckCircle, FloppyDisk } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/permissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const dayNum = (iso) => Number(iso.slice(8, 10));
const monthKey = (iso) => iso.slice(0, 7);
const monthLabel = (key) => `${MONTH[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;

function DayCell({ day, cap, onPick }) {
  const cls =
    day.status === "off"
      ? "bg-slate-100 text-slate-400 border-slate-200"
      : day.status === "busy"
      ? "bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100";
  return (
    <button
      type="button"
      onClick={() => day.jobs.length && onPick(day)}
      className={`aspect-square rounded-md border p-1 flex flex-col items-center justify-center transition-colors ${cls} ${
        day.jobs.length ? "cursor-pointer hover:ring-2 hover:ring-orange-400" : ""
      }`}
      data-testid={`load-day-${day.date}`}
      title={day.status === "off" ? "Weekly off" : day.status === "busy" ? `${day.hours} of ${cap} h booked` : "Free"}
    >
      <span className="text-xs font-semibold leading-none">{dayNum(day.date)}</span>
      {day.status === "busy" && <span className="text-[10px] mt-0.5 leading-none">{day.hours}h</span>}
      {day.status === "free" && <span className="text-[10px] mt-0.5 leading-none">free</span>}
    </button>
  );
}

function MachineCalendar({ row, cap }) {
  const [picked, setPicked] = useState(null);
  const months = useMemo(() => {
    const out = {};
    (row.schedule || []).forEach((d) => {
      const k = monthKey(d.date);
      (out[k] = out[k] || []).push(d);
    });
    return out;
  }, [row.schedule]);

  return (
    <Card className="border-slate-200 shadow-none" data-testid={`load-machine-${row.machine_code}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <Gear className="h-4 w-4 text-slate-500" weight="bold" />
            <span className="font-mono text-sm">{row.machine_code}</span>
            <span className="truncate">{row.machine_name}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">{row.machine_type}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {row.pending_hours > 0 ? (
            <>
              <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200" data-testid={`load-pending-${row.machine_code}`}>
                {row.pending_hours} h pending · {row.busy_days} busy day{row.busy_days === 1 ? "" : "s"}
              </Badge>
              {row.free_from && (
                <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`load-free-from-${row.machine_code}`}>
                  Free from {new Date(row.free_from).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                </Badge>
              )}
            </>
          ) : (
            <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`load-idle-${row.machine_code}`}>
              <CheckCircle className="h-3.5 w-3.5 mr-1" weight="fill" /> No pending load
            </Badge>
          )}
        </div>
      </div>

      <div className="p-4 space-y-5">
        {picked && (
          <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 sticky top-2 z-10 shadow-sm" data-testid="load-day-detail">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-slate-900">
                {row.machine_code} · {row.machine_name} —{" "}
                {new Date(picked.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · {picked.hours} h
              </span>
              <button onClick={() => setPicked(null)} className="text-xs text-slate-500 hover:text-slate-900" data-testid="load-day-close">Close</button>
            </div>
            <ul className="space-y-1 text-xs">
              {picked.jobs.map((j, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span><span className="font-mono text-blue-700">{j.job_code}</span> · {j.operation_name}</span>
                  <span className="font-medium">{j.hours} h</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {Object.entries(months).map(([key, days]) => {
          const lead = new Date(days[0].date).getDay();
          return (
            <div key={key}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{monthLabel(key)}</div>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_LABELS.map((d) => (
                  <div key={d} className="text-[10px] text-center text-slate-400 font-medium">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: lead }).map((_, i) => <div key={`p${i}`} />)}
                {days.map((d) => <DayCell key={d.date} day={d} cap={cap} onPick={setPicked} />)}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function MachineLoad() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user?.role);

  const settings = useQuery({
    queryKey: ["capacity-settings"],
    queryFn: async () => (await api.get("/capacity/settings")).data,
  });

  const [dailyHours, setDailyHours] = useState("");
  const [grace, setGrace] = useState("");
  const [shiftStart, setShiftStart] = useState("");
  const [shiftEnd, setShiftEnd] = useState("");
  const [off, setOff] = useState(null);
  const [machineFilter, setMachineFilter] = useState("all");
  const [onlyBusy, setOnlyBusy] = useState(false);

  const cap = dailyHours !== "" ? Number(dailyHours) : settings.data?.daily_hours ?? 11;
  const offDays = off ?? settings.data?.weekly_off ?? [0];

  const load = useQuery({
    queryKey: ["machine-load", cap, offDays.join(","), 60],
    queryFn: async () =>
      (await api.get("/capacity/machine-load", { params: { daily_hours: cap, weekly_off: offDays.join(","), days: 60 } })).data,
    enabled: !!settings.data,
  });

  const rows = useMemo(() => {
    let r = load.data?.machines || [];
    if (machineFilter !== "all") r = r.filter((m) => m.machine_id === machineFilter);
    if (onlyBusy) r = r.filter((m) => m.pending_hours > 0);
    return r;
  }, [load.data, machineFilter, onlyBusy]);

  const toggleOff = (d) => {
    const next = offDays.includes(d) ? offDays.filter((x) => x !== d) : [...offDays, d].sort();
    if (next.length >= 7) { toast.error("At least one working day is required"); return; }
    setOff(next);
  };

  const save = async () => {
    try {
      await api.put("/capacity/settings", {
        daily_hours: cap,
        weekly_off: offDays,
        grace_minutes: Number(grace !== "" ? grace : settings.data?.grace_minutes ?? 30),
        shift_start: shiftStart || settings.data?.shift_start || "09:00",
        shift_end: shiftEnd || settings.data?.shift_end || "20:00",
      });
      qc.invalidateQueries({ queryKey: ["capacity-settings"] });
      toast.success("Capacity settings saved for everyone");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const busyCount = (load.data?.machines || []).filter((m) => m.pending_hours > 0).length;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Machine Load</h1>
        <p className="text-sm text-slate-500 mt-1">
          Pending planned hours from active jobs, spread across the calendar at {cap} h a day.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none p-4" data-testid="load-settings">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Working hours / day</label>
            <Input type="number" inputMode="decimal" min="1" max="24" className="mt-1.5 w-28"
              value={dailyHours !== "" ? dailyHours : settings.data?.daily_hours ?? ""}
              onChange={(e) => setDailyHours(e.target.value)} data-testid="load-daily-hours" />
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Idle grace (min)</label>
            <Input type="number" inputMode="numeric" min="1" max="240" className="mt-1.5 w-28"
              value={grace !== "" ? grace : settings.data?.grace_minutes ?? ""}
              onChange={(e) => setGrace(e.target.value)} data-testid="load-grace-minutes" />
            <p className="text-[11px] text-slate-400 mt-1">After this idle gap the next task auto-starts for the operator.</p>
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Shift window</label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input type="time" className="w-28"
                value={shiftStart !== "" ? shiftStart : settings.data?.shift_start ?? ""}
                onChange={(e) => setShiftStart(e.target.value)} data-testid="load-shift-start" />
              <span className="text-slate-400 text-sm">to</span>
              <Input type="time" className="w-28"
                value={shiftEnd !== "" ? shiftEnd : settings.data?.shift_end ?? ""}
                onChange={(e) => setShiftEnd(e.target.value)} data-testid="load-shift-end" />
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Auto-start never runs outside this window.</p>
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Weekly off</label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {DAY_LABELS.map((d, i) => (
                <button key={d} type="button" onClick={() => toggleOff(i)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    offDays.includes(i) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`load-off-${i}`}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2 sm:col-span-2 xl:col-span-4 pt-1 border-t border-slate-100 mt-1 w-full">
            <Select value={machineFilter} onValueChange={setMachineFilter}>
              <SelectTrigger className="w-[210px]" data-testid="load-machine-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All machines</SelectItem>
                {(load.data?.machines || []).map((m) => (
                  <SelectItem key={m.machine_id} value={m.machine_id}>{m.machine_code} · {m.machine_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant={onlyBusy ? "default" : "outline"} onClick={() => setOnlyBusy((v) => !v)}
              className={onlyBusy ? "bg-orange-600 hover:bg-orange-700" : ""} data-testid="load-only-busy">
              Busy only ({busyCount})
            </Button>
            {admin && (
              <Button variant="outline" onClick={save} data-testid="load-save-settings">
                <FloppyDisk className="h-4 w-4 mr-1.5" /> Save as default
              </Button>
            )}
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-orange-200 border border-orange-300" /> Busy</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-100 border border-emerald-200" /> Free</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-slate-100 border border-slate-200" /> Weekly off</span>
          <span className="font-medium text-slate-600">Tap a busy day to see the jobs loaded on it.</span>
        </div>
      </Card>

      {onlyBusy && (load.data?.machines || []).some((m) => m.pending_hours > 0) && (
        <Card className="border-orange-200 bg-orange-50/50 shadow-none p-4" data-testid="load-busy-list">
          <div className="text-xs font-semibold uppercase tracking-wide text-orange-800 mb-2">
            Busy machines ({busyCount}) — tap a name to see only that machine
          </div>
          <div className="flex flex-wrap gap-2">
            {(load.data?.machines || []).filter((m) => m.pending_hours > 0).map((m) => (
              <button key={m.machine_id} type="button" onClick={() => setMachineFilter(m.machine_id === machineFilter ? "all" : m.machine_id)}
                className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  machineFilter === m.machine_id
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-slate-700 border-orange-200 hover:border-orange-400"
                }`}
                data-testid={`load-busy-chip-${m.machine_code}`}>
                <span className="font-mono">{m.machine_code}</span> · {m.machine_name}
                <span className={machineFilter === m.machine_id ? "text-orange-100" : "text-slate-500"}> · {m.pending_hours} h</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {load.isLoading ? (
        <div className="text-slate-400 py-20 text-center">Loading machine load...</div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400 border-slate-200 shadow-none" data-testid="load-empty">
          <CalendarBlank className="h-8 w-8 mx-auto mb-2" />
          No machines match this filter.
        </Card>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {rows.map((r) => <MachineCalendar key={r.machine_id} row={r} cap={cap} />)}
        </div>
      )}
    </div>
  );
}
